import React, { useState } from 'react'
import {
    CircularProgress,
    IconButton,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import CloseIcon from '@material-ui/icons/Close'
import BlockIcon from '@material-ui/icons/Block'
import DeleteIcon from '@material-ui/icons/Delete'

import {
    AreaConfig,
    Damage,
    DamageConfig,
    DamageStatus,
    DamageTransaction,
    DELETE_DAMAGES_PERMISSION,
    InventoryConfig,
    ModelMap,
    Unit,
    WorkSpaceUser,
} from '../../models'
import { axiosInstance } from '../../helpers'
import { toast } from 'react-toastify'
import { UpdateDamageRequest } from '../../hooks'
import { DamageRow } from './DamageRow'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { hasPermission } from '../../models/Users/services'

type DuplicateCluster = { original: Damage; duplicates: Damage[] }

interface Props {
    open: boolean
    loading: boolean
    results: { strict: DuplicateCluster[]; possible: DuplicateCluster[] }
    workspaceUser?: WorkSpaceUser
    damageConfigList?: DamageConfig[]
    areaConfigMap?: ModelMap<AreaConfig>
    invConfigMap?: ModelMap<InventoryConfig>
    unitMap?: ModelMap<Unit>
    onClose: () => void
    onUpdateResults: (
        results: (prev: {
            strict: DuplicateCluster[]
            possible: DuplicateCluster[]
        }) => { strict: DuplicateCluster[]; possible: DuplicateCluster[] },
    ) => void
    handleStatusChange: (
        damages: Damage[],
        status: DamageStatus,
    ) => Promise<void> | void | undefined
    removeDamageTransaction: (
        transaction: DamageTransaction,
        damage: Damage,
    ) => void
    onOpenMessageDrawer: (channelId: number, damageId: number) => void
    updateDamage: (
        damage: Damage,
        request: UpdateDamageRequest,
    ) => Promise<Damage | void>
    openPopover: (
        event: React.MouseEvent<HTMLElement>,
        damageId: number,
    ) => void
}

const PAGE_SIZE = 5

export const DuplicateBlanket = ({
    open,
    loading,
    results,
    workspaceUser,
    damageConfigList,
    areaConfigMap,
    invConfigMap,
    unitMap,
    onClose,
    onUpdateResults,
    handleStatusChange,
    removeDamageTransaction,
    onOpenMessageDrawer,
    updateDamage,
    openPopover,
}: Props) => {
    const theme = useTheme()
    const [activeTab, setActiveTab] = useState<'strict' | 'possible'>('strict')
    const [pages, setPages] = useState<{ strict: number; possible: number }>({
        strict: 1,
        possible: 1,
    })

    const removeDup = (
        set: 'strict' | 'possible',
        clusterIdx: number,
        dupId: number,
    ) => {
        onUpdateResults((prev) => {
            const clusters = [...prev[set]]
            clusters[clusterIdx] = {
                ...clusters[clusterIdx],
                duplicates: clusters[clusterIdx].duplicates.filter(
                    (d) => d.id !== dupId,
                ),
            }
            return {
                ...prev,
                [set]: clusters.filter((c) => c.duplicates.length > 0),
            }
        })
    }

    const handleDelete = (
        set: 'strict' | 'possible',
        clusterIdx: number,
        dupId: number,
    ) => {
        axiosInstance
            .delete(`lease/damage/${dupId}/`)
            .then(() => {
                removeDup(set, clusterIdx, dupId)
                toast.success(`Damage #${dupId} deleted`)
            })
            .catch((e) =>
                toast.error(`Failed to delete damage #${dupId}: ${e.message}`),
            )
    }

    const handleMarkNotDuplicate = (
        set: 'strict' | 'possible',
        clusterIdx: number,
        dupId: number,
    ) => {
        axiosInstance
            .patch(`lease/damage/${dupId}/`, { not_a_duplicate: true })
            .then(() => {
                removeDup(set, clusterIdx, dupId)
                toast.success(`Damage #${dupId} marked as not a duplicate`)
            })
            .catch((e) =>
                toast.error(`Failed to dismiss damage #${dupId}: ${e.message}`),
            )
    }

    const canDelete = hasPermission(workspaceUser, DELETE_DAMAGES_PERMISSION)
    const activeClusters = results[activeTab]
    const currentPage = pages[activeTab]
    const pagedClusters = activeClusters.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE,
    )

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1300,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.default,
                transform: open ? 'translateY(0)' : 'translateY(100%)',
                transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: open ? 'auto' : 'none',
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    flexShrink: 0,
                    gap: theme.spacing(1),
                }}
            >
                <LoriIcon width={28} height={28} />
                <Typography variant="h6">Duplicate Scan Results</Typography>
                <div style={{ flex: 1 }} />
                <Tooltip title="Close">
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </div>

            {/* Tabs */}
            {!loading && (
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => {
                        setActiveTab(v)
                        setPages((p) => ({ ...p, [v]: 1 }))
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                    style={{
                        flexShrink: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <Tab
                        value="strict"
                        label={`High Probability (${results.strict.reduce(
                            (a, c) => a + c.duplicates.length,
                            0,
                        )})`}
                    />
                    <Tab
                        value="possible"
                        label={`Medium Probability (${results.possible.reduce(
                            (a, c) => a + c.duplicates.length,
                            0,
                        )})`}
                    />
                </Tabs>
            )}

            {/* Content */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: theme.spacing(1),
                }}
            >
                {loading ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <CircularProgress size={48} />
                    </div>
                ) : activeClusters.length === 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography variant="h6" color="textSecondary">
                            No {activeTab === 'possible' ? 'possible ' : ''}
                            duplicates found
                        </Typography>
                    </div>
                ) : (
                    pagedClusters.map((cluster, pageIdx) => {
                        const clusterIdx =
                            (currentPage - 1) * PAGE_SIZE + pageIdx
                        return (
                            <div
                                key={`cluster-${clusterIdx}`}
                                style={{
                                    marginBottom: theme.spacing(3),
                                    borderRadius: theme.shape.borderRadius,
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                }}
                            >
                                {/* Original */}
                                <div
                                    style={{
                                        borderLeft: `4px solid ${theme.palette.success.main}`,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            padding: `${theme.spacing(
                                                0.75,
                                            )}px ${theme.spacing(1.5)}px`,
                                            backgroundColor: `${theme.palette.success.main}18`,
                                            borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                        }}
                                    >
                                        <span
                                            style={
                                                {
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    backgroundColor:
                                                        theme.palette.success
                                                            .main,
                                                    color:
                                                        theme.palette.success
                                                            .contrastText,
                                                    borderRadius: 99,
                                                    padding: '2px 10px',
                                                    ...theme.typography.caption,
                                                    fontWeight:
                                                        theme.typography
                                                            .fontWeightBold,
                                                    letterSpacing: '0.05em',
                                                } as React.CSSProperties
                                            }
                                        >
                                            Original
                                        </span>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            #{cluster.original.id}
                                        </Typography>
                                    </div>
                                    <DamageRow
                                        damage={cluster.original}
                                        selected={false}
                                        displayProblems={false}
                                        onSelect={() => {}}
                                        handleStatusChange={handleStatusChange}
                                        removeDamageTransaction={
                                            removeDamageTransaction
                                        }
                                        onOpenMessageDrawer={
                                            onOpenMessageDrawer
                                        }
                                        updateDamage={updateDamage}
                                        openPopover={openPopover}
                                        damageConfigList={damageConfigList}
                                        areaConfigMap={areaConfigMap}
                                        invConfigMap={invConfigMap}
                                        unitMap={unitMap}
                                    />
                                </div>

                                {/* Possible duplicates */}
                                {cluster.duplicates.map((dup, dupIdx) => (
                                    <div
                                        key={`dup-${dup.id}`}
                                        style={{
                                            borderLeft: `4px solid ${theme.palette.warning.main}`,
                                            borderTop:
                                                dupIdx === 0
                                                    ? `2px solid ${theme.palette.grey[300]}`
                                                    : `1px solid ${theme.palette.grey[200]}`,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                padding: `${theme.spacing(
                                                    0.75,
                                                )}px ${theme.spacing(1.5)}px`,
                                                backgroundColor: `${theme.palette.warning.main}12`,
                                                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                            }}
                                        >
                                            <span
                                                style={
                                                    {
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        backgroundColor:
                                                            theme.palette
                                                                .warning.main,
                                                        color:
                                                            theme.palette
                                                                .warning
                                                                .contrastText,
                                                        borderRadius: 99,
                                                        padding: '2px 10px',
                                                        ...theme.typography
                                                            .caption,
                                                        fontWeight:
                                                            theme.typography
                                                                .fontWeightBold,
                                                        letterSpacing: '0.05em',
                                                    } as React.CSSProperties
                                                }
                                            >
                                                Possible Duplicate
                                            </span>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                #{dup.id}
                                            </Typography>
                                            <div style={{ flex: 1 }} />
                                            <Tooltip title="Not a duplicate">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleMarkNotDuplicate(
                                                            activeTab,
                                                            clusterIdx,
                                                            dup.id,
                                                        )
                                                    }
                                                >
                                                    <BlockIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    canDelete
                                                        ? 'Delete duplicate'
                                                        : 'No permission to delete'
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={!canDelete}
                                                        onClick={() =>
                                                            handleDelete(
                                                                activeTab,
                                                                clusterIdx,
                                                                dup.id,
                                                            )
                                                        }
                                                        style={{
                                                            color: canDelete
                                                                ? theme.palette
                                                                      .error
                                                                      .main
                                                                : undefined,
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </div>
                                        <DamageRow
                                            damage={dup}
                                            selected={false}
                                            displayProblems={false}
                                            onSelect={() => {}}
                                            handleStatusChange={
                                                handleStatusChange
                                            }
                                            removeDamageTransaction={
                                                removeDamageTransaction
                                            }
                                            onOpenMessageDrawer={
                                                onOpenMessageDrawer
                                            }
                                            updateDamage={updateDamage}
                                            openPopover={openPopover}
                                            damageConfigList={damageConfigList}
                                            areaConfigMap={areaConfigMap}
                                            invConfigMap={invConfigMap}
                                            unitMap={unitMap}
                                        />
                                    </div>
                                ))}
                            </div>
                        )
                    })
                )}
            </div>

            {/* Pagination */}
            {!loading && activeClusters.length > PAGE_SIZE && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: theme.spacing(1),
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        flexShrink: 0,
                    }}
                >
                    <Pagination
                        count={Math.ceil(activeClusters.length / PAGE_SIZE)}
                        page={currentPage}
                        onChange={(_, value) =>
                            setPages((p) => ({ ...p, [activeTab]: value }))
                        }
                        color="primary"
                    />
                </div>
            )}
        </div>
    )
}
